import { z } from "zod";

import { isCarouselSocialPostType, type SocialPostType } from "./social-post-scene.ts";

export const aiCreditPolicySchema = z.preprocess(
  (value) => {
    if (!value || typeof value !== "object" || Array.isArray(value)) {
      return value;
    }

    const {
      freeStandaloneAssetsPerBrand: _legacyFreeAssets,
      freeCampaignPreviewRegenerations: _legacyFreeCampaignPreviewRegenerations,
      ...policy
    } = value as Record<string, unknown>;
    return policy;
  },
  z
    .object({
      enabled: z.boolean(),
      costs: z.object({
        standaloneSinglePostGeneration: z.number().int().nonnegative().default(2),
        standaloneCarouselGeneration: z.number().int().nonnegative().default(6),
        campaignSinglePostGeneration: z.number().int().nonnegative().default(2),
        campaignCarouselGeneration: z.number().int().nonnegative().default(8),
        socialPostRegeneration: z.number().int().nonnegative().default(1),
        campaignPreviewRegeneration: z.number().int().nonnegative(),
        assistantImageGeneration: z.number().int().nonnegative().default(1),
      }),
    })
    .strict(),
);

export type AiCreditPolicy = z.infer<typeof aiCreditPolicySchema>;

export const DEFAULT_AI_CREDIT_POLICY = {
  enabled: false,
  costs: {
    standaloneSinglePostGeneration: 2,
    standaloneCarouselGeneration: 6,
    campaignSinglePostGeneration: 2,
    campaignCarouselGeneration: 8,
    socialPostRegeneration: 1,
    campaignPreviewRegeneration: 4,
    assistantImageGeneration: 1,
  },
} satisfies AiCreditPolicy;

export const getStandaloneGenerationRunCost = (
  policy: AiCreditPolicy,
  postType: SocialPostType,
): number =>
  isCarouselSocialPostType(postType)
    ? policy.costs.standaloneCarouselGeneration
    : policy.costs.standaloneSinglePostGeneration;

export const getCampaignGenerationRunCost = (
  policy: AiCreditPolicy,
  postType: SocialPostType,
): number =>
  isCarouselSocialPostType(postType)
    ? policy.costs.campaignCarouselGeneration
    : policy.costs.campaignSinglePostGeneration;
